/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatSession } from '../../store';
import { useUIState } from '../../store';
import { MActionItemsList } from '../MActionItemsList';
import * as Styled from './MListNavActions.styles';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';

interface MListNavActionsProps {
  chat: ChatSession;
  isActive: boolean;
  onSwitch: (chatId: string) => void;
  onRename: (chatId: string, newTitle: string) => void;
  onDelete: (chatId: string) => void;
  formatRelativeTime: (date: Date) => string;
}

export const MListNavActions: React.FC<MListNavActionsProps> = ({
  chat,
  isActive,
  onSwitch,
  onRename,
  onDelete,
  formatRelativeTime: _formatRelativeTime,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(false);
  const menuRef = useRef<HTMLLIElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useUIState();

  const handleSaveRename = useCallback(() => {
    if (renameValue.trim()) {
      onRename(chat.id, renameValue.trim());
    }
    setIsRenaming(false);
    setRenameValue('');
    setActiveDropdown(false);
  }, [renameValue, onRename, chat.id]);

  useEffect(() => {
    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveDropdown(false);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handlePointerOutside);
      document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    const handleRenameClickOutside = (event: MouseEvent | TouchEvent) => {
      if (renameInputRef.current && !renameInputRef.current.contains(event.target as Node)) {
        handleSaveRename();
      }
    };

    if (isRenaming) {
      document.addEventListener('mousedown', handleRenameClickOutside);
      document.addEventListener('touchstart', handleRenameClickOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleRenameClickOutside);
      document.removeEventListener('touchstart', handleRenameClickOutside);
    };
  }, [isRenaming, handleSaveRename]);

  const handleRename = () => {
    setIsRenaming(true);
    setRenameValue(chat.title);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setRenameValue('');
    setActiveDropdown(false);
  };

  return (
    <Styled.NavActionWrapper
      ref={menuRef}
      isActive={isActive}
      data-component-name="m-web-ui-kit-MListNavActions"
      data-testid="web-ui-kit-MListNavActions"
      data-track-label={AI_SEARCH_TRACK_LABELS.CHAT_HISTORY}
    >
      <Styled.NavItemWrapper isActive={isActive}>
        {isRenaming ? (
          <Styled.RenameForm
            className="d-flex flex-grow-1 min-width-0 nav-item-wrapper"
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSaveRename();
            }}
            onClick={e => e.stopPropagation()}
          >
            <Styled.RenameInput
              ref={renameInputRef}
              type="text"
              value={renameValue}
              maxLength={50}
              enterKeyHint="done"
              autoComplete="off"
              aria-label={`Rename chat ${chat.title}`}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') handleCancelRename();
                e.stopPropagation();
              }}
            />
          </Styled.RenameForm>
        ) : (
          <Styled.NavItemSelectButton
            type="button"
            className="nav-item-wrapper"
            aria-label={chat.title}
            aria-current={isActive ? 'true' : undefined}
            onClick={() => onSwitch(chat.id)}
          >
            <Styled.Text className={isMobile ? 't-font-m' : 't-font-s'} data-session-id={chat.tipWorkflowId}>
              {chat.title}
            </Styled.Text>
          </Styled.NavItemSelectButton>
        )}
        <Styled.ChatActions isActive={isActive} className="chat-actions">
          <Styled.NavAction
            type="button"
            aria-label={`More options for ${chat.title}`}
            aria-expanded={activeDropdown}
            aria-haspopup="true"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setActiveDropdown(!activeDropdown);
            }}
          >
            <AIcon icon="icon-more" size={AIconSize.S} aria-hidden="true"></AIcon>
          </Styled.NavAction>
        </Styled.ChatActions>
      </Styled.NavItemWrapper>

      {!isRenaming && (
        <MActionItemsList activeDropdown={activeDropdown} onRename={handleRename} onDelete={() => onDelete(chat.id)} />
      )}
    </Styled.NavActionWrapper>
  );
};
